import { createContext, useCallback, useContext, type ReactNode } from "react";

import { Navigate, useLocation } from "react-router-dom";

import { LoadingScreen } from "@components/title-slide-view";
import { useUser } from "@components/user-context";
import { useNorthbeamMutation, useNorthbeamQuery } from "@hooks/http";
import {
  type CreateShortlink_createShortlink,
  type CreateShortlink,
  type CreateShortlinkVariables,
  type FetchShortlink,
  type FetchShortlinkVariables,
} from "@north-beam/nb-client-side";

import { CREATE_SHORTLINK, GET_SHORTLINK } from "./gql";

type ShortlinksContextType = {
  id: string | null;
  url: string | null;
  loading: boolean;
  createShortlink: (
    url: string,
  ) => Promise<CreateShortlink_createShortlink | undefined>;
  createShortlinkLoading: boolean;
  createShortlinkData?: CreateShortlink_createShortlink;
};
export const ShortlinksContext = createContext<
  ShortlinksContextType | undefined
>(undefined);

const useShortlinkId = () => {
  const { pathname } = useLocation();
  // /<user-id>/share/<shortlink-id>
  const shortlinkIdPathIndex = pathname.split("/").indexOf("share");
  const shortlinkId =
    shortlinkIdPathIndex > -1
      ? pathname.split("/")[shortlinkIdPathIndex + 1]
      : undefined;
  return shortlinkId ? `${pathname.split("/").pop()}` : null;
};
export const ShortlinksProvider = ({ children }: { children: ReactNode }) => {
  const shortlinkId = useShortlinkId();

  const { user } = useUser();
  const { data, loading } = useNorthbeamQuery<
    FetchShortlink,
    FetchShortlinkVariables
  >(GET_SHORTLINK, {
    variables: {
      shortlinkId: shortlinkId ?? "",
    },
    skip: !shortlinkId,
  });

  const [
    createShortlinkInternal,
    { loading: createShortlinkLoading, data: createShortlinkData },
  ] = useNorthbeamMutation<CreateShortlink, CreateShortlinkVariables>(
    CREATE_SHORTLINK,
  );

  const createShortlink = useCallback(
    async (url: string) => {
      const result = await createShortlinkInternal({ variables: { url } });
      return result.data?.createShortlink;
    },
    [createShortlinkInternal],
  );

  if (data?.shortlink) {
    const currentUrl = new URL(window.location.href);
    const base = `${currentUrl.protocol}//${currentUrl.host}`;
    const fullUrl = `${base}/${user.displayUserId}/${data.shortlink.url}`;
    const newUrl = new URL(fullUrl);
    return (
      <Navigate to={{ pathname: newUrl.pathname, search: newUrl.search }} />
    );
  }

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <ShortlinksContext.Provider
      value={{
        id: data?.shortlink?.id ?? null,
        url: data?.shortlink?.url ?? null,
        loading,
        createShortlink,
        createShortlinkLoading,
        createShortlinkData: createShortlinkData?.createShortlink,
      }}
    >
      {children}
    </ShortlinksContext.Provider>
  );
};

export const useShortLinksContext = () => {
  const context = useContext(ShortlinksContext);
  if (!context) {
    throw new Error(
      "useShortLinksContext must be used within a ShortlinksProvider",
    );
  }
  return context;
};
